import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Smoke Particles Component
function SmokeParticles({ rocketPosition }) {
  const particlesRef = useRef();
  const particleCount = 50;

  const particles = useMemo(() => {
    const positions = new Float32Array(particleCount * 3);
    const velocities = [];
    const lifetimes = [];
    
    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = 0;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = 0;
      velocities.push({
        x: (Math.random() - 0.5) * 0.02,
        y: -Math.random() * 0.03 - 0.01,
        z: (Math.random() - 0.5) * 0.02,
      });
      lifetimes.push(Math.random() * 100);
    }
    
    return { positions, velocities, lifetimes };
  }, [particleCount]);

  useFrame(() => {
    if (!particlesRef.current) return;
    
    const positions = particlesRef.current.geometry.attributes.position.array;
    
    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      
      // Update particle lifetime
      particles.lifetimes[i] -= 1;
      
      // Reset particle if lifetime is over
      if (particles.lifetimes[i] <= 0) {
        positions[i3] = rocketPosition.current.x;
        positions[i3 + 1] = rocketPosition.current.y - 0.3;
        positions[i3 + 2] = rocketPosition.current.z;
        particles.lifetimes[i] = 100;
        particles.velocities[i] = {
          x: (Math.random() - 0.5) * 0.02,
          y: -Math.random() * 0.03 - 0.01,
          z: (Math.random() - 0.5) * 0.02,
        };
      } else {
        // Move particle
        positions[i3] += particles.velocities[i].x;
        positions[i3 + 1] += particles.velocities[i].y;
        positions[i3 + 2] += particles.velocities[i].z;
      }
    }
    
    particlesRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={particles.positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.25}
        color="#999999"
        transparent
        opacity={0.8}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

// Fallback Rocket Component
function FallbackRocket() {
  return (
    <group scale={2}>
      {/* Body */}
      <mesh position={[0, 0, 0]}>
        <cylinderGeometry args={[0.4, 0.4, 2, 32]} />
        <meshStandardMaterial color="#e74c3c" metalness={0.6} roughness={0.3} />
      </mesh>
      {/* Nose cone */}
      <mesh position={[0, 1.4, 0]}>
        <coneGeometry args={[0.4, 0.8, 32]} />
        <meshStandardMaterial color="#c0392b" metalness={0.6} roughness={0.3} />
      </mesh>
      {/* Fins - 4 fins around the base */}
      <mesh position={[0.6, -0.8, 0]} rotation={[0, 0, Math.PI / 4]}>
        <boxGeometry args={[0.8, 0.08, 0.5]} />
        <meshStandardMaterial color="#34495e" metalness={0.7} roughness={0.2} />
      </mesh>
      <mesh position={[-0.6, -0.8, 0]} rotation={[0, 0, -Math.PI / 4]}>
        <boxGeometry args={[0.8, 0.08, 0.5]} />
        <meshStandardMaterial color="#34495e" metalness={0.7} roughness={0.2} />
      </mesh>
      <mesh position={[0, -0.8, 0.6]} rotation={[Math.PI / 4, 0, 0]}>
        <boxGeometry args={[0.8, 0.08, 0.5]} />
        <meshStandardMaterial color="#34495e" metalness={0.7} roughness={0.2} />
      </mesh>
      <mesh position={[0, -0.8, -0.6]} rotation={[-Math.PI / 4, 0, 0]}>
        <boxGeometry args={[0.8, 0.08, 0.5]} />
        <meshStandardMaterial color="#34495e" metalness={0.7} roughness={0.2} />
      </mesh>
      {/* Window */}
      <mesh position={[0, 0.4, 0.42]}>
        <circleGeometry args={[0.2, 32]} />
        <meshStandardMaterial color="#3498db" emissive="#3498db" emissiveIntensity={1.5} />
      </mesh>
      {/* Exhaust glow */}
      <mesh position={[0, -1.2, 0]}>
        <cylinderGeometry args={[0.2, 0.08, 0.4, 32]} />
        <meshStandardMaterial color="#ff6b35" emissive="#ff6b35" emissiveIntensity={3} />
      </mesh>
      {/* Stripe decoration */}
      <mesh position={[0, -0.3, 0]}>
        <cylinderGeometry args={[0.42, 0.42, 0.3, 32]} />
        <meshStandardMaterial color="#f39c12" metalness={0.8} roughness={0.2} />
      </mesh>
    </group>
  );
}

// Animated Rocket Component
function AnimatedRocket() {
  const rocketRef = useRef();
  const rocketPosition = useRef({ x: 0, y: 0, z: 0 });
  
  // Random movement parameters
  const movementParams = useRef({
    offsetX: Math.random() * Math.PI * 2,
    offsetY: Math.random() * Math.PI * 2,
    speedX: 0.001,
    speedY: 0.0015,
    amplitudeX: 4,
    amplitudeY: 2.5,
  });

  useFrame(({ clock }) => {
    if (!rocketRef.current) return;
    
    const t = clock.getElapsedTime();
    const params = movementParams.current;
    
    // Smooth random movement using sine waves
    const x = Math.sin(t * params.speedX + params.offsetX) * params.amplitudeX;
    const y = Math.sin(t * params.speedY + params.offsetY) * params.amplitudeY;
    
    rocketRef.current.position.x = x;
    rocketRef.current.position.y = y;
    
    // Store position for smoke particles
    rocketPosition.current = {
      x: rocketRef.current.position.x,
      y: rocketRef.current.position.y,
      z: rocketRef.current.position.z,
    };
    
    // Rotate rocket slightly based on movement
    rocketRef.current.rotation.z = -x * 0.08;
    rocketRef.current.rotation.x = y * 0.04;
    
    // Slow rotation
    rocketRef.current.rotation.y += 0.01;
  });

  return (
    <>
      <group ref={rocketRef} position={[0, 0, 0]}>
        <FallbackRocket />
      </group>
      <SmokeParticles rocketPosition={rocketPosition} />
    </>
  );
}

// Main Background Component
export const RocketBackground = () => {
  console.log('🚀 RocketBackground component mounted');
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: -1,
      pointerEvents: 'none',
      opacity: 0.85,
    }}>
      <Canvas
        camera={{ position: [0, 0, 12], fov: 65 }}
        style={{ background: 'transparent' }}
        gl={{ alpha: true, antialias: true }}
        onCreated={({ gl, scene, camera }) => {
          console.log('🎨 Three.js Canvas created');
          console.log('✅ WebGL Renderer:', gl);
          console.log('✅ Scene:', scene);
          console.log('✅ Camera:', camera);
        }}
        onError={(error) => {
          console.error('❌ Three.js Canvas error:', error);
        }}
      >
        <ambientLight intensity={2} />
        <directionalLight position={[10, 10, 10]} intensity={2} castShadow />
        <directionalLight position={[-10, -10, -10]} intensity={1.5} />
        <pointLight position={[0, 0, 5]} intensity={2} color="#ffffff" />
        <AnimatedRocket />
      </Canvas>
    </div>
  );
};
